import {
  type InviteInfo,
  type InviteResponse,
  inviteInfoSchema,
  inviteResponseSchema,
  loginResponseSchema,
  type MeResponse,
  meResponseSchema,
  mfaRequiredSchema,
  type PasskeySummary,
  type PasswordResetInfo,
  type PasswordResetResponse,
  passkeyListSchema,
  passwordResetInfoSchema,
  passwordResetRequestedSchema,
  passwordResetResponseSchema,
  publicKeyOptionsResponseSchema,
  type SessionUser,
  sessionListSchema,
  type TotpSetup,
  totpSetupSchema,
  type UserSummary,
  userListSchema,
  userSummarySchema,
} from '@acasa/shared'
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser'
import { startAuthentication, startRegistration } from '@simplewebauthn/browser'
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiRequestError, apiFetch } from '@/lib/api'

/**
 * Sign-in, enrolment and account administration.
 *
 * The two ceremonies each take two round trips — ask the server for options,
 * hand them to the browser, send back what the authenticator produced — and
 * the server holds the challenge in between. Nothing here ever sees a private
 * key: the browser will not release one, which is the entire point.
 */

export const sessionKey = ['auth', 'me'] as const

export function useSession() {
  const query = useQuery({
    queryKey: sessionKey,
    queryFn: () => apiFetch<typeof meResponseSchema>('/auth/me', meResponseSchema),
    // Checked again on focus: an admin can disable an account mid-shift, and
    // the tab should not keep pretending to be signed in.
    refetchOnWindowFocus: true,
    staleTime: 60_000,
    retry: false,
  })

  const data = query.data as MeResponse | undefined

  return {
    ...query,
    user: data?.user ?? null,
    // False while loading, so the login page never promises an e-mail on a
    // guess — the "ask an administrator" text is the safe one to flash.
    mailConfigured: data?.mail.configured ?? false,
  }
}

/**
 * Who is signed in changes on login, enrolment and logout; whether the server
 * can send mail does not. Carrying the cached flag over keeps those three from
 * having to invent one — and a logged-out login page from forgetting it.
 */
function setSessionUser(
  queryClient: QueryClient,
  user: SessionUser | null,
  mail = queryClient.getQueryData<MeResponse>(sessionKey)?.mail,
) {
  queryClient.setQueryData(sessionKey, {
    user,
    mail: mail ?? { configured: false },
  } satisfies MeResponse)
}

/* ---------------------------------------------------------------- login --- */

async function passkeyCeremony(optionsPath: string, verifyPath: string, email?: string) {
  const { options } = await apiFetch(optionsPath, publicKeyOptionsResponseSchema, {
    method: 'POST',
    body: email ? { email } : {},
  })

  const credential = await startAuthentication({
    optionsJSON: options as unknown as PublicKeyCredentialRequestOptionsJSON,
  })

  return apiFetch(verifyPath, loginResponseSchema, {
    method: 'POST',
    body: { credential },
  })
}

export function useLoginWithPasskey() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (email?: string) =>
      passkeyCeremony('/auth/login/passkey/options', '/auth/login/passkey/verify', email),
    onSuccess: (data) => {
      setSessionUser(queryClient, data.user)
    },
  })
}

/**
 * Step one of the fallback. Deliberately returns no session — a correct password
 * only buys the right to attempt the second factor.
 */
export function useLoginWithPassword() {
  return useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      apiFetch('/auth/login/password', mfaRequiredSchema, { method: 'POST', body: input }),
  })
}

/** Step two. No e-mail — the server knows the account from the ticket cookie. */
export function useLoginWithTotp() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (code: string) =>
      apiFetch('/auth/login/totp', loginResponseSchema, { method: 'POST', body: { code } }),
    onSuccess: (data) => {
      setSessionUser(queryClient, data.user)
    },
  })
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (input: { currentPassword: string; password: string }) => {
      const response = await fetch('/api/v1/auth/password', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      })
      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as {
          error?: { message?: string }
        } | null
        throw new ApiRequestError(
          response.status,
          'password_change_failed',
          payload?.error?.message ?? 'Passwort konnte nicht geändert werden',
        )
      }
    },
  })
}

/* ------------------------------------------------------- password reset --- */

/**
 * "Passwort vergessen?". The server answers `{ requested: true }` whatever the
 * address, so nothing here can tell — or show — whether an account exists.
 */
export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: (email: string) =>
      apiFetch('/auth/password-reset', passwordResetRequestedSchema, {
        method: 'POST',
        body: { email },
      }),
  })
}

export function usePasswordResetInfo(token: string | undefined) {
  return useQuery({
    queryKey: ['auth', 'password-reset', token],
    queryFn: () => apiFetch(`/auth/password-reset/${token}`, passwordResetInfoSchema),
    enabled: Boolean(token),
    // A 404 is the answer (dead link), not a blip worth three more requests.
    retry: false,
  })
}

/**
 * Answers 204 and deliberately does NOT sign anybody in: the link replaces the
 * password, and the authenticator code is still asked at the login page.
 */
export function useResetPassword(token: string | undefined) {
  return useMutation({
    mutationFn: async (password: string) => {
      const response = await fetch(`/api/v1/auth/password-reset/${token}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      })
      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as {
          error?: { message?: string }
        } | null
        throw new ApiRequestError(
          response.status,
          'password_reset_failed',
          payload?.error?.message ?? 'Passwort konnte nicht gesetzt werden',
        )
      }
    },
  })
}

export function useLogout() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      await fetch('/api/v1/auth/logout', { method: 'POST', credentials: 'include' })
    },
    onSuccess: () => {
      // Everything cached was fetched as somebody. Clear it all rather than
      // leaving another person's estate list on screen behind a login form.
      const mail = queryClient.getQueryData<MeResponse>(sessionKey)?.mail
      queryClient.clear()
      setSessionUser(queryClient, null, mail)
    },
  })
}

/* ------------------------------------------------------------ enrolment --- */

export function useInvite(token: string | undefined) {
  return useQuery({
    queryKey: ['auth', 'invite', token],
    queryFn: () => apiFetch(`/auth/invite/${token}`, inviteInfoSchema),
    enabled: Boolean(token),
    retry: false,
  })
}

export type EnrolmentStep = 'passkey' | 'totp' | 'done'

/** Step one of enrolment: the password. Answers with the TOTP secret. */
export function useEnrolPassword(token: string | undefined) {
  return useMutation({
    mutationFn: (password: string): Promise<TotpSetup> =>
      apiFetch(`/auth/invite/${token}/password`, totpSetupSchema, {
        method: 'POST',
        body: { password },
      }),
  })
}

export function useConfirmTotp(token: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (code: string) =>
      apiFetch(`/auth/invite/${token}/totp/confirm`, loginResponseSchema, {
        method: 'POST',
        body: { code },
      }),
    onSuccess: (data) => {
      setSessionUser(queryClient, data.user)
    },
  })
}

/* ------------------------------------------------------ own credentials --- */

export function usePasskeys() {
  return useQuery({
    queryKey: ['auth', 'passkeys'],
    queryFn: () => apiFetch('/auth/passkeys', passkeyListSchema),
  })
}

export function useAddPasskey() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (label?: string) => {
      const { options } = await apiFetch('/auth/passkeys/options', publicKeyOptionsResponseSchema, {
        method: 'POST',
        body: {},
      })

      const credential = await startRegistration({
        optionsJSON: options as unknown as PublicKeyCredentialCreationOptionsJSON,
      })

      await fetch('/api/v1/auth/passkeys/verify', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential, label }),
      }).then(async (response) => {
        if (!response.ok) {
          const payload = (await response.json().catch(() => null)) as {
            error?: { message?: string }
          } | null
          throw new ApiRequestError(
            response.status,
            'passkey_failed',
            payload?.error?.message ?? 'Passkey konnte nicht gespeichert werden',
          )
        }
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['auth'] })
    },
  })
}

export function useRemovePasskey() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/v1/auth/passkeys/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        credentials: 'include',
      })
      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as {
          error?: { message?: string }
        } | null
        throw new ApiRequestError(
          response.status,
          'delete_failed',
          payload?.error?.message ?? 'Passkey konnte nicht entfernt werden',
        )
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['auth', 'passkeys'] })
    },
  })
}

/* ---------------------------------------------------------- own sessions --- */

export function useSessions() {
  return useQuery({
    queryKey: ['auth', 'sessions'],
    queryFn: () => apiFetch('/auth/sessions', sessionListSchema),
  })
}

export function useRevokeSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/v1/auth/sessions/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        credentials: 'include',
      })
      if (!response.ok) {
        throw new ApiRequestError(
          response.status,
          'revoke_failed',
          'Sitzung konnte nicht beendet werden',
        )
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['auth', 'sessions'] })
    },
  })
}

export function useRevokeOtherSessions() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const response = await fetch('/api/v1/auth/sessions/revoke-others', {
        method: 'POST',
        credentials: 'include',
      })
      if (!response.ok) {
        throw new ApiRequestError(
          response.status,
          'revoke_failed',
          'Geräte konnten nicht abgemeldet werden',
        )
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['auth', 'sessions'] })
    },
  })
}

/* ------------------------------------------------------- administration --- */

export function useUsers(enabled: boolean) {
  return useQuery({
    queryKey: ['auth', 'users'],
    queryFn: () => apiFetch('/auth/users', userListSchema),
    enabled,
  })
}

export function useCreateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: { email: string; name: string; role: 'admin' | 'agent' }) =>
      apiFetch('/auth/users', inviteResponseSchema, { method: 'POST', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['auth', 'users'] })
    },
  })
}

export function useReinvite() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/auth/users/${id}/invite`, inviteResponseSchema, { method: 'POST', body: {} }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['auth', 'users'] })
    },
  })
}

/** An admin issuing a reset link — the copy-it-from-here path while no mail goes out. */
export function useIssuePasswordReset() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/auth/users/${id}/password-reset`, passwordResetResponseSchema, {
        method: 'POST',
        body: {},
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['auth', 'users'] })
    },
  })
}

export function useUpdateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      ...patch
    }: {
      id: string
      name?: string
      role?: 'admin' | 'agent'
      status?: 'active' | 'disabled'
    }) => apiFetch(`/auth/users/${id}`, userSummarySchema, { method: 'PATCH', body: patch }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['auth', 'users'] })
    },
  })
}

export function useDeleteUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/v1/auth/users/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      })
      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as {
          error?: { message?: string }
        } | null
        throw new ApiRequestError(
          response.status,
          'delete_failed',
          payload?.error?.message ?? 'Konto konnte nicht gelöscht werden',
        )
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['auth', 'users'] })
    },
  })
}

export type {
  InviteInfo,
  InviteResponse,
  PasskeySummary,
  PasswordResetInfo,
  PasswordResetResponse,
  SessionUser,
  TotpSetup,
  UserSummary,
}
